/*!
 * Knockout Foreach Binding 🥊  tko.binding.foreach@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { arrayForEach, cleanNode, options, virtualElements, createSymbolOrString, domData, domNodeIsContainedBy } from 'tko.utils';
import { isObservable, unwrap, observable } from 'tko.observable';
import { contextFor, applyBindingsToDescendants, AsyncBindingHandler } from 'tko.bind';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

// index.js
//      Utilities
var MAX_LIST_SIZE = 9007199254740991;
// from https://github.com/jonschlinkert/is-plain-object
function isPlainObject(o) {
    return !!o && typeof o === 'object' && o.constructor === Object;
}
var supportsDocumentFragment = options.document && typeof options.document.createDocumentFragment === 'function';
// Get a copy of the (possibly virtual) child nodes of the given element,
// put them into a container, then empty the given node.
function makeTemplateNode(sourceNode) {
    var container = document.createElement('div');
    var parentNode;
    if (sourceNode.content) {
        // For e.g. <template> tags
        parentNode = sourceNode.content;
    }
    else if (sourceNode.tagName === 'SCRIPT') {
        parentNode = document.createElement('div');
        parentNode.innerHTML = sourceNode.text;
    }
    else {
        // Anything else e.g. <div>
        parentNode = sourceNode;
    }
    arrayForEach(virtualElements.childNodes(parentNode), function (child) {
        // FIXME - This cloneNode could be expensive; we may prefer to iterate over the
        // parentNode children in reverse (so as not to foul the indexes as childNodes are
        // removed from parentNode when inserted into the container)
        if (child) {
            container.insertBefore(child.cloneNode(true), null);
        }
    });
    return container;
}
// Mimic a KO change item 'add'
function valueToChangeAddItem(value, index) {
    return {
        status: 'added',
        value: value,
        index: index
    };
}
// store a symbol for caching the pending delete info index in the data item objects
var PENDING_DELETE_INDEX_SYM = createSymbolOrString('_ko_ffe_pending_delete_index');
var ForEachBinding = /** @class */ (function (_super) {
    __extends(ForEachBinding, _super);
    // NOTE: valid valueAccessors include:
    //    []
    //    observable([])
    //    observableArray([])
    //    computed
    //    {data: array, name: string, as: string}
    function ForEachBinding(params) {
        var _this = _super.call(this, params) || this;
        var settings = {};
        if (isPlainObject(_this.value)) {
            Object.assign(settings, _this.value);
        }
        _this.as = settings.as || _this.allBindings.get('as');
        _this.data = settings.data || (unwrap(_this.$context.$rawData) === _this.value ? _this.$context.$rawData : _this.value);
        _this.container = virtualElements.isStartComment(_this.$element)
            ? _this.$element.parentNode : _this.$element;
        _this.generateContext = _this.createContextGenerator(_this.as);
        _this.$indexHasBeenRequested = false;
        _this.templateNode = makeTemplateNode(settings.templateNode || (settings.name
            ? document.getElementById(settings.name).cloneNode(true)
            : _this.$element));
        ['afterAdd', 'beforeRemove', 'afterQueueFlush', 'beforeQueueFlush']
            .forEach(function (p) { _this[p] = settings[p] || _this.allBindings.get(p); });
        _this.changeQueue = [];
        _this.firstLastNodesList = [];
        _this.indexesToDelete = [];
        _this.rendering_queued = false;
        _this.pendingDeletes = [];
        // Expose the conditional so that if the `foreach` data is empty, successive
        // 'else' bindings will appear.
        _this.isNotEmpty = observable(Boolean(unwrap(_this.data).length));
        domData.set(_this.$element, 'conditional', {
            elseChainSatisfied: _this.isNotEmpty
        });
        // Remove existing content.
        virtualElements.emptyNode(_this.$element);
        // Prime content
        var primeData = unwrap(_this.data);
        if (primeData.map) {
            _this.onArrayChange(primeData.map(valueToChangeAddItem), true);
        }
        else {
            _this.completeBinding();
        }
        // Watch for changes
        if (isObservable(_this.data)) {
            if (!_this.data.indexOf) {
                // Make sure the observable is trackable.
                _this.data = _this.data.extend({ trackArrayChanges: true });
            }
            _this.changeSubs = _this.data.subscribe(_this.onArrayChange, _this, 'arrayChange');
        }
        return _this;
    }
    ForEachBinding.prototype.dispose = function () {
        if (this.changeSubs) {
            this.changeSubs.dispose();
        }
        this.flushPendingDeletes();
    };
    // If the array changes we register the change.
    ForEachBinding.prototype.onArrayChange = function (changeSet, isInitial) {
        var _this = this;
        var changeMap = {
            added: [],
            deleted: []
        };
        // knockout array change notification index handling:
        // - sends the original array indexes for deletes
        // - sends the new array indexes for adds
        // - sorts them all by index in ascending order
        // because of this, when checking for possible batch additions, any delete can be between to adds with neighboring indexes, so only additions should be checked
        for (var i = 0, len = changeSet.length; i < len; i++) {
            if (changeMap.added.length && changeSet[i].status === 'added') {
                var lastAdd = changeMap.added[changeMap.added.length - 1];
                var lastIndex = lastAdd.isBatch ? lastAdd.index + lastAdd.values.length - 1 : lastAdd.index;
                if (lastIndex + 1 === changeSet[i].index) {
                    if (!lastAdd.isBatch) {
                        // transform the last addition into a batch addition object
                        lastAdd = {
                            isBatch: true,
                            status: 'added',
                            index: lastAdd.index,
                            values: [lastAdd.value]
                        };
                        changeMap.added.splice(changeMap.added.length - 1, 1, lastAdd);
                    }
                    lastAdd.values.push(changeSet[i].value);
                    continue;
                }
            }
            changeMap[changeSet[i].status].push(changeSet[i]);
        }
        if (changeMap.deleted.length > 0) {
            this.changeQueue.push.apply(this.changeQueue, changeMap.deleted);
            this.changeQueue.push({ status: 'clearDeletedIndexes' });
        }
        this.changeQueue.push.apply(this.changeQueue, changeMap.added);
        // Once a change is registered, the ticking count-down starts for the processQueue.
        if (this.changeQueue.length > 0 && !this.rendering_queued) {
            this.rendering_queued = true;
            if (isInitial) {
                this.processQueue();
            }
            else {
                ForEachBinding.animateFrame.call(window, function () { return _this.processQueue(); });
            }
        }
    };
    ForEachBinding.prototype.startQueueFlush = function () {
        // Callback so folks can do things before the queue flush.
        if (typeof this.beforeQueueFlush === 'function') {
            this.beforeQueueFlush(this.changeQueue);
        }
    };
    ForEachBinding.prototype.endQueueFlush = function () {
        // Callback so folks can do things.
        if (typeof this.afterQueueFlush === 'function') {
            this.afterQueueFlush(this.changeQueue);
        }
    };
    // Reflect all the changes in the queue in the DOM, then wipe the queue.
    ForEachBinding.prototype.processQueue = function () {
        var _this = this;
        var isEmpty = !unwrap(this.data).length;
        var lowestIndexChanged = MAX_LIST_SIZE;
        this.startQueueFlush();
        arrayForEach(this.changeQueue, function (changeItem) {
            if (typeof changeItem.index === 'number') {
                lowestIndexChanged = Math.min(lowestIndexChanged, changeItem.index);
            }
            _this[changeItem.status](changeItem);
        });
        this.flushPendingDeletes();
        this.rendering_queued = false;
        // Update our indexes.
        if (this.$indexHasBeenRequested) {
            this.updateIndexes(lowestIndexChanged);
        }
        this.endQueueFlush();
        this.changeQueue = [];
        // Update the conditional exposed on the domData
        if (isEmpty !== !this.isNotEmpty()) {
            this.isNotEmpty(!isEmpty);
        }
    };
    /**
     * Once the $index has been asked for once, start calculating it.
     * Note that this significantly degrades performance, from O(1) to O(n)
     * for arbitrary changes to the list.
     */
    ForEachBinding.prototype._first$indexRequest = function (ctx$indexRequestedFrom) {
        this.$indexHasBeenRequested = true;
        for (var i = 0, len = this.firstLastNodesList.length; i < len; ++i) {
            var ctx = this.getContextStartingFrom(this.firstLastNodesList[i].first);
            // Overwrite the defineProperty.
            if (ctx) {
                ctx.$index = observable(i);
            }
        }
        return ctx$indexRequestedFrom.$index();
    };
    ForEachBinding.prototype._contextExtensions = function ($ctx) {
        var _this = this;
        Object.assign($ctx, { $list: this.data });
        if (this.$indexHasBeenRequested) {
            $ctx.$index = $ctx.$index || observable();
        }
        else {
            Object.defineProperty($ctx, '$index', {
                value: function () { return _this._first$indexRequest($ctx); },
                configurable: true,
                writable: true
            });
        }
        return $ctx;
    };
    /**
     * Return a function that generates the context for a given node.
     *
     * We generate a single function that reduces our inner-loop calculations,
     * which has a good chance of being optimized by the browser.
     *
     * @param  {string} as  The name given to each item in the list
     * @param  {bool} index Whether to calculate indexes
     * @return {function}   A function(dataValue) that returns the context
     */
    ForEachBinding.prototype.createContextGenerator = function (as) {
        var _this = this;
        var $ctx = this.$context;
        if (as) {
            return function (v) {
                var _a;
                return _this._contextExtensions($ctx.extend((_a = {}, _a[as] = v, _a)));
            };
        }
        else {
            return function (v) { return $ctx.createChildContext(v, null, function (ctx) { return _this._contextExtensions(ctx); }); };
        }
    };
    ForEachBinding.prototype.updateFirstLastNodesList = function (index, children) {
        var first = children[0];
        var last = children[children.length - 1];
        this.firstLastNodesList.splice(index, 0, { first: first, last: last });
    };
    // Process a changeItem with {status: 'added', ...}
    ForEachBinding.prototype.added = function (changeItem) {
        var index = changeItem.index;
        var valuesToAdd = changeItem.isBatch ? changeItem.values : [changeItem.value];
        var referenceElement = this.getLastNodeBeforeIndex(index);
        // gather all childnodes for a possible batch insertion
        var allChildNodes = [];
        var asyncBindingResults = [];
        var children;
        for (var i = 0, len = valuesToAdd.length; i < len; ++i) {
            // we check if we have a pending delete with reusable nodesets for this data, and if yes, we reuse one nodeset
            var pendingDelete = this.getPendingDeleteFor(valuesToAdd[i]);
            if (pendingDelete && pendingDelete.nodesets.length) {
                children = pendingDelete.nodesets.pop();
                this.updateFirstLastNodesList(index + i, children);
            }
            else {
                var templateClone = this.templateNode.cloneNode(true);
                children = virtualElements.childNodes(templateClone);
                this.updateFirstLastNodesList(index + i, children);
                // Apply bindings first, and then process child nodes,
                // because bindings can add childnodes.
                var bindingResult = applyBindingsToDescendants(this.generateContext(valuesToAdd[i]), templateClone);
                asyncBindingResults.push(bindingResult);
            }
            allChildNodes.push.apply(allChildNodes, __spread(children));
        }
        if (typeof this.afterAdd === 'function') {
            this.afterAdd({
                nodeOrArrayInserted: this.insertAllAfter(allChildNodes, referenceElement),
                foreachInstance: this
            });
        }
        else {
            this.insertAllAfter(allChildNodes, referenceElement);
        }
        this.completeBinding(Promise.all(asyncBindingResults));
    };
    ForEachBinding.prototype.getNodesForIndex = function (index) {
        var result = [];
        var ptr = this.firstLastNodesList[index].first;
        var last = this.firstLastNodesList[index].last;
        result.push(ptr);
        while (ptr && ptr !== last) {
            ptr = ptr.nextSibling;
            result.push(ptr);
        }
        return result;
    };
    ForEachBinding.prototype.getLastNodeBeforeIndex = function (index) {
        if (index < 1 || index - 1 >= this.firstLastNodesList.length) {
            return null;
        }
        return this.firstLastNodesList[index - 1].last;
    };
    /**
     * Get the active (focused) node, if it's a child of the given node.
     */
    ForEachBinding.prototype.activeChildElement = function (node) {
        var active = document.activeElement;
        if (domNodeIsContainedBy(active, node)) {
            return active;
        }
    };
    ForEachBinding.prototype.insertAllAfter = function (nodeOrNodeArrayToInsert, insertAfterNode) {
        var frag;
        var len;
        var i;
        var active = null;
        var containerNode = this.$element;
        // Poor man's node and array check.
        if (nodeOrNodeArrayToInsert.nodeType === undefined && nodeOrNodeArrayToInsert.length === undefined) {
            throw new Error('Expected a single node or a node array');
        }
        if (nodeOrNodeArrayToInsert.nodeType !== undefined) {
            active = this.activeChildElement(nodeOrNodeArrayToInsert);
            virtualElements.insertAfter(containerNode, nodeOrNodeArrayToInsert, insertAfterNode);
            return [nodeOrNodeArrayToInsert];
        }
        else if (nodeOrNodeArrayToInsert.length === 1) {
            active = this.activeChildElement(nodeOrNodeArrayToInsert[0]);
            virtualElements.insertAfter(containerNode, nodeOrNodeArrayToInsert[0], insertAfterNode);
        }
        else if (supportsDocumentFragment) {
            frag = document.createDocumentFragment();
            for (i = 0, len = nodeOrNodeArrayToInsert.length; i !== len; ++i) {
                active = active || this.activeChildElement(nodeOrNodeArrayToInsert[i]);
                frag.appendChild(nodeOrNodeArrayToInsert[i]);
            }
            virtualElements.insertAfter(containerNode, frag, insertAfterNode);
        }
        else {
            // Nodes are inserted in reverse order - pushed down immediately after
            // the last node for the previous item or as the first node of element.
            for (i = nodeOrNodeArrayToInsert.length - 1; i >= 0; --i) {
                active = active || this.activeChildElement(nodeOrNodeArrayToInsert[i]);
                var child = nodeOrNodeArrayToInsert[i];
                if (!child) {
                    break;
                }
                virtualElements.insertAfter(containerNode, child, insertAfterNode);
            }
        }
        if (active) {
            active.focus();
        }
        return nodeOrNodeArrayToInsert;
    };
    // checks if the deleted data item should be handled with delay for a possible reuse at additions
    ForEachBinding.prototype.shouldDelayDeletion = function (data) {
        return data && (typeof data === 'object' || typeof data === 'function');
    };
    // gets the pending deletion info for this data item
    ForEachBinding.prototype.getPendingDeleteFor = function (data) {
        var index = data && data[PENDING_DELETE_INDEX_SYM];
        if (index === undefined)
            return null;
        return this.pendingDeletes[index];
    };
    // tries to find the existing pending delete info for this data item, and if it can't, it registeres one
    ForEachBinding.prototype.getOrCreatePendingDeleteFor = function (data) {
        var pd = this.getPendingDeleteFor(data);
        if (pd) {
            return pd;
        }
        pd = {
            data: data,
            nodesets: []
        };
        data[PENDING_DELETE_INDEX_SYM] = this.pendingDeletes.length;
        this.pendingDeletes.push(pd);
        return pd;
    };
    // Process a changeItem with {status: 'deleted', ...}
    ForEachBinding.prototype.deleted = function (changeItem) {
        // if we should delay the deletion of this data, we add the nodeset to the pending delete info object
        if (this.shouldDelayDeletion(changeItem.value)) {
            var pd = this.getOrCreatePendingDeleteFor(changeItem.value);
            pd.nodesets.push(this.getNodesForIndex(changeItem.index));
        }
        else { // simple data, just remove the nodes
            this.removeNodes(this.getNodesForIndex(changeItem.index));
        }
        this.indexesToDelete.push(changeItem.index);
    };
    // removes a set of nodes from the DOM
    ForEachBinding.prototype.removeNodes = function (nodes) {
        if (!nodes.length) {
            return;
        }
        function removeFn() {
            var parent = nodes[0].parentNode;
            for (var i = nodes.length - 1; i >= 0; --i) {
                cleanNode(nodes[i]);
                parent.removeChild(nodes[i]);
            }
        }
        if (this.beforeRemove) {
            var beforeRemoveReturn = this.beforeRemove({
                nodesToRemove: nodes, foreachInstance: this
            }) || {};
            // If beforeRemove returns a `then`–able e.g. a Promise, we remove
            // the nodes when that thenable completes.  We pass any errors to
            // ko.onError.
            if (typeof beforeRemoveReturn.then === 'function') {
                beforeRemoveReturn.then(removeFn, options.onError);
            }
        }
        else {
            removeFn();
        }
    };
    // flushes the pending delete info store
    // this should be called after queue processing has finished, so that data items and remaining (not reused) nodesets get cleaned up
    // we also call it on dispose not to leave any mess
    ForEachBinding.prototype.flushPendingDeletes = function () {
        for (var i = 0, len = this.pendingDeletes.length; i !== len; ++i) {
            var pd = this.pendingDeletes[i];
            while (pd.nodesets.length) {
                this.removeNodes(pd.nodesets.pop());
            }
            if (pd.data && pd.data[PENDING_DELETE_INDEX_SYM] !== undefined) {
                delete pd.data[PENDING_DELETE_INDEX_SYM];
            }
        }
        this.pendingDeletes = [];
    };
    // We batch our deletion of item indexes in our parallel array.
    // See brianmhunt/knockout-fast-foreach#6/#8
    ForEachBinding.prototype.clearDeletedIndexes = function () {
        // We iterate in reverse on the presumption (following the unit tests) that KO's diff engine
        // processes diffs (esp. deletes) monotonically ascending i.e. from index 0 -> N.
        for (var i = this.indexesToDelete.length - 1; i >= 0; --i) {
            this.firstLastNodesList.splice(this.indexesToDelete[i], 1);
        }
        this.indexesToDelete = [];
    };
    ForEachBinding.prototype.updateIndexes = function (fromIndex) {
        var ctx;
        for (var i = fromIndex, len = this.firstLastNodesList.length; i < len; ++i) {
            ctx = this.getContextStartingFrom(this.firstLastNodesList[i].first);
            if (ctx) {
                ctx.$index(i);
            }
        }
    };
    ForEachBinding.prototype.getContextStartingFrom = function (node) {
        var ctx;
        while (node) {
            ctx = contextFor(node);
            if (ctx) {
                return ctx;
            }
            node = node.nextSibling;
        }
    };
    /**
     * Set whether the binding is always synchronous.
     * Useful during testing.
     */
    ForEachBinding.setSync = function (toggle) {
        var w = options.global;
        if (toggle) {
            ForEachBinding.animateFrame = function (frame) { frame(); };
        }
        else {
            ForEachBinding.animateFrame = w.requestAnimationFrame || w.webkitRequestAnimationFrame ||
                w.mozRequestAnimationFrame || w.msRequestAnimationFrame ||
                function (cb) { return w.setTimeout(cb, 1000 / 60); };
        }
    };
    Object.defineProperty(ForEachBinding.prototype, "controlsDescendants", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ForEachBinding, "allowVirtualElements", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ForEachBinding, "ForEach", {
        /* TODO: Remove; for legacy/testing */
        get: function () { return this; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ForEachBinding, "PENDING_DELETE_INDEX_SYM", {
        get: function () { return PENDING_DELETE_INDEX_SYM; },
        enumerable: true,
        configurable: true
    });
    return ForEachBinding;
}(AsyncBindingHandler));

var bindings = {
    foreach: ForEachBinding
};
// By default, foreach will be async.
ForEachBinding.setSync(false);

export { bindings };
//# sourceMappingURL=tko.binding.foreach.js.map
